import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { components } from "@/types/api.generated";
import type { CollegeGoalHorizon } from "@/types/entities";
export type { CollegeGoal } from "@/types/entities";
import { toast } from "sonner";

export interface CollegeGoalFilters {
  college_strategic_pillar_id?: string;
  horizon?: CollegeGoalHorizon;
  cursor?: string;
  limit?: number;
}

export function useCollegeGoals(filters: CollegeGoalFilters = {}) {
  return useQuery({
    queryKey: ["college-goals", filters],
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/college-goals", {
        params: { query: filters },
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
  });
}

export function useCreateCollegeGoal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["CreateCollegeGoalRequest"]) => {
      const { data, error } = await apiClient.POST("/v1/college-goals", { body });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["college-goals"] });
      void queryClient.invalidateQueries({ queryKey: ["college-strategic-pillars"] });
      toast.success("College goal created");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateCollegeGoal(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["UpdateCollegeGoalRequest"]) => {
      const { data, error } = await apiClient.PUT("/v1/college-goals/{id}", {
        params: { path: { id } },
        body,
      });
      if (error) throw new Error(error.error.message);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["college-goals"] });
      void queryClient.invalidateQueries({ queryKey: ["college-strategic-pillars"] });
      toast.success("College goal updated");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useDeleteCollegeGoal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/college-goals/{id}", {
        params: { path: { id } },
      });
      if (error) throw new Error(error.error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["college-goals"] });
      void queryClient.invalidateQueries({ queryKey: ["college-strategic-pillars"] });
      toast.success("College goal deleted");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
